import { defineStore } from 'pinia'
import { store } from '../index'
import { useCache } from '@/hooks/web/useCache'
import { appModules } from '@/config/app'
import type { AppState, LayoutType, ChartColor } from '@/config/app'
import { setCssVar, humpToUnderline } from '@/utils'
import { ElMessage } from 'element-plus'

const { wsCache } = useCache()
export const useAppStore = defineStore({
  id: 'app',
  state: (): AppState => appModules,
  persist: {
    enabled: true
  },
  getters: {
    getVersion(): string {
      return this.version
    },
    getBreadcrumb(): boolean {
      return this.breadcrumb
    },
    getBreadcrumbIcon(): boolean {
      return this.breadcrumbIcon
    },
    getCollapse(): boolean {
      return this.collapse
    },
    getUniqueOpened(): boolean {
      return this.uniqueOpened
    },
    getHamburger(): boolean {
      return this.hamburger
    },
    getScreenfull(): boolean {
      return this.screenfull
    },
    getSize(): boolean {
      return this.size
    },
    getLocale(): boolean {
      return this.locale
    },
    getTagsView(): boolean {
      return this.tagsView
    },
    getTagsViewIcon(): boolean {
      return this.tagsViewIcon
    },
    getLogo(): boolean {
      return this.logo
    },
    // 右上角logo
    getWiseLogo(): string {
      return this.wiseLogo
    },
    // 网页favicon
    getFavicon(): string {
      return this.favicon
    },
    // 登录背景页Back
    getBack(): string {
      return this.back
    },
    // 登录用户
    getUserName(): string {
      return this.userName
    },
    // 登录密码
    getPassword(): string {
      return this.password
    },
    getFixedHeader(): boolean {
      return this.fixedHeader
    },
    getGreyMode(): boolean {
      return this.greyMode
    },
    getPageLoading(): boolean {
      return this.pageLoading
    },
    getLayout(): LayoutType {
      return this.layout
    },
    getTitle(): string {
      return this.title
    },
    getUserInfo(): string {
      return this.userInfo
    },
    getIsDark(): boolean {
      return this.isDark
    },
    getCurrentSize(): ElememtPlusSize {
      return this.currentSize
    },
    getSizeMap(): ElememtPlusSize[] {
      return this.sizeMap
    },
    getMobile(): boolean {
      return this.mobile
    },
    getTheme(): string {
      return this.theme
    },
    getFooter(): boolean {
      return this.footer
    },
    getDynamicRouter(): boolean {
      return this.dynamicRouter
    },
    getHideMenu(): boolean {
      return this.hideMenu
    },
    getHideHeader(): boolean {
      return this.hideHeader
    },
    getHideDialog(): boolean {
      return this.hideDialog
    },
    getChartColor(): ChartColor{
      return this.chartColor
    }    
  },
  actions: {
    setBreadcrumb(breadcrumb: boolean) {
      this.breadcrumb = breadcrumb
    },
    setBreadcrumbIcon(breadcrumbIcon: boolean) {
      this.breadcrumbIcon = breadcrumbIcon
    },
    setCollapse(collapse: boolean) {
      this.collapse = collapse
    },
    setUniqueOpened(uniqueOpened: boolean) {
      this.uniqueOpened = uniqueOpened
    },
    setHamburger(hamburger: boolean) {
      this.hamburger = hamburger
    },
    setScreenfull(screenfull: boolean) {
      this.screenfull = screenfull
    },
    setSize(size: boolean) {
      this.size = size
    },
    setLocale(locale: boolean) {
      this.locale = locale
    },
    setTagsView(tagsView: boolean) {
      this.tagsView = tagsView
    },
    setTagsViewIcon(tagsViewIcon: boolean) {
      this.tagsViewIcon = tagsViewIcon
    },
    setLogo(logo: boolean) {
      this.logo = logo
    },
    setwiseLogo(wiseLogo: string) {
      this.wiseLogo = wiseLogo
    },
    setfavicon(favicon: string) {
      this.favicon = favicon
    },
    setback(back: string) {
      this.back = back
    },
    // 登录用户
    setUserName(userName: string) {
      this.userName = userName
    },
    // 登录密码
    setPassword(password: string) {
      this.password = password
    },
    setFixedHeader(fixedHeader: boolean) {
      this.fixedHeader = fixedHeader
    },
    setGreyMode(greyMode: boolean) {
      this.greyMode = greyMode
    },
    setPageLoading(pageLoading: boolean) {
      this.pageLoading = pageLoading
    },
    setLayout(layout: LayoutType) {
      if (this.mobile && layout !== 'classic') {
        ElMessage.warning('移动端模式下不支持切换其他布局')
        return
      }
      this.layout = layout
      wsCache.set('layout', this.layout)
    },
    setTitle(title: string) {
      this.title = title
    },
    setIsDark(isDark: boolean) {
      this.isDark = isDark
      if (this.isDark) {
        document.documentElement.classList.add('dark')
        document.documentElement.classList.remove('light')
      } else {
        document.documentElement.classList.add('light')
        document.documentElement.classList.remove('dark')
      }
      wsCache.set('isDark', this.isDark)
    },
    setCurrentSize(currentSize: ElememtPlusSize) {
      this.currentSize = currentSize
      wsCache.set('currentSize', this.currentSize)
    },
    setMobile(mobile: boolean) {
      this.mobile = mobile
    },
    setTheme(theme: string) {      
      wsCache.set('theme', this.theme)       
      if(theme=='light'){
        this.chartColor = {
          text:'#323233',
          label:'#787878',
          title:'#555',
          line:'#E1E1E1',
          border:'#C8C8C8',  
          legendTxt:'#787878',

          blue: '#3381D6',
          green: '#00B900',
          red: '#DF001A',
          orange: '#FF9300',
          yellow: '#EBC147',
          gray:'#787878',
          brightBlue: '#EBC147',
          purple: '#8048C8',
          limeGreen: '#4BD8A5',
          brightpink: '#FB4788',          
          greenBg: '#E1F7E1',
          redBg: '#FBE1E4',
          orangeBg: '#FFEFDA', 

          popperBorder: '#FFF', 
          popperBg: '#FFF',         
        } 
      }else{
        this.chartColor = {
          text:'#B9C1C4',
          label:'#85969B',
          title:'#9FACB0',
          line:'rgba(81, 100, 107, 0.60)',
          border:'#51646B',
          legendTxt:'#85969B',

          blue: '#3381D6',
          green: '#33C733',
          red: '#E53348',
          orange: '#FF9300',
          yellow: '#EBC147',
          gray:'#85969B',
          brightBlue: '#EBC147',
          purple: '#8048C8',
          limeGreen: '#4BD8A5',
          brightpink: '#FB4788',          
          greenBg: '#095309',
          redBg: '#5C0711',
          orangeBg: '#6F5B22', 

          popperBorder: '#17293C', 
          popperBg: '#17293C', 
        }
      }
      this.theme = theme
      console.log('---setTheme', theme, this.chartColor)
    },
    setCssVarTheme() {
      for (const key in this.theme) {
        setCssVar(`--${humpToUnderline(key)}`, this.theme[key])
      }
    },
    setFooter(footer: boolean) {
      this.footer = footer
    },
    setHideMenu(val: boolean) {
      this.hideMenu = val
    },
    setHideHeader(val: boolean) {
      this.hideHeader = val
    },
    setHideDialog(val: boolean) {
      this.hideDialog = val
    }
  }
})

export const useAppStoreWithOut = () => {
  return useAppStore(store)
}
